import { useMemo } from 'react'

import { ScoredCreditCardProduct } from '@mediaingenuity/totallymoney.types'
import { useRouter } from 'next/router'

import Box from '@ilg-totallymoney/ui/components/Box'
import Divider from '@ilg-totallymoney/ui/components/Divider'
import Flex from '@ilg-totallymoney/ui/components/Flex'
import Grid from '@ilg-totallymoney/ui/components/Grid'
import Heading from '@ilg-totallymoney/ui/components/Heading'
import LinkButton from '@ilg-totallymoney/ui/components/LinkButton'
import LinkWithChevron from '@ilg-totallymoney/ui/components/LinkWithChevron'
import WaveDivider from '@ilg-totallymoney/ui/components/WaveDivider'

import { GuaranteedOfferType } from '@appTypes/App.interface'
import {
    ApplicationURLTriggerLocation,
    ScoredCreditCardProductsCategory,
} from '@appTypes/EligibilityApi.interface'

import BadgesRow from '@components/BadgesRow'
import CardImage from '@components/CardImage'
import CardPageProductLists from '@components/CardPageProductLists'
import CardPositivesInfo from '@components/CardPositivesInfo'
import CreditCardContent from '@components/CreditCardContent/CreditCardContent'
import EligibilityIndicatorWithModal from '@components/EligibilityIndicatorWithModal'
import GuaranteedOfferModal from '@components/GuaranteedOfferModal'
import PreApprovedBadgeWithModal from '@components/PreApprovedBadgeWithModal/PreApprovedBadgeWithModal'
import RatesAndFees from '@components/RatesAndFees'
import RepresentativeExample from '@components/RepresentativeExample'
import RepresentativeExampleModal from '@components/RepresentativeExampleModal'

import { LoggerKey } from '@config/logger'

import useAppQueryState from '@hooks/useAppQueryState'
import useInscalarTrackEvent from '@hooks/useInscalarTrackEvent'
import useLogEvents from '@hooks/useLogEvents'
import useLogEvents2 from '@hooks/useLogEvents2'
import useLogger from '@hooks/useLogger'
import useSetState from '@hooks/useSetState'

import { getProductInfoText } from '@routes/ScoredTableRoute/components/ScoredTable/components/ScoredTableItem/utils/getProductInfoText'
import { categorySortKeyComparatorKeys } from '@routes/ScoredTableRoute/components/ScoredTable/utils/categorySortKeyComparatorKeys'
import { getDownsellCreditCards } from '@routes/ScoredTableRoute/components/ScoredTable/utils/getDownsellCreditCards'
import { getValidCategorySortKey } from '@routes/ScoredTableRoute/components/ScoredTable/utils/getValidCategorySortKey'
import useGetCreditCardApplicationUrl from '@routes/ScoredTableRoute/hooks/useGetCardApplicationUrl'
import useGetDifferenceInNewCreditCardsOffers from '@routes/ScoredTableRoute/hooks/useGetDifferenceInNewCreditCardsOffers'
import { getSponsoredProducts } from '@routes/ScoredTableRoute/utils/getSponsoredProducts'

import { borderStyle } from '@styles/borderStyle'

import { buildAcquisitionJourneyScoredTableClickoutEventPayload } from '@utils/buildAcquisitionEvents'
import { getAppConfig } from '@utils/getAppConfig'
import getCategoryByCategoryId from '@utils/getCategoryByCategoryId'
import getGuaranteedOfferType from '@utils/getGuaranteedOfferType'
import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'
import { getShowOfferDurationContent } from '@utils/getShowOfferDurationContent'
import { getUuid } from '@utils/getUuid'
import { sortCardsBySortKey } from '@utils/sortCardsBySortKey'

import ScoredCardDetailsTotallySureTooltipBanner from './components/ScoredCardDetailsTotallySureTooltipBanner'

interface ScoredCardProps {
    scoredCard: ScoredCreditCardProduct
    scoredCategories?: ScoredCreditCardProductsCategory[]
    thirdPartyReference: string
}

enum ModalType {
    GuaranteedOfferModal = 'GuaranteedOfferModal',
    RepresentativeExample = 'RepresentativeExample',
}

interface ModalState {
    activeCard: ScoredCreditCardProduct | null
    activeModal: ModalType | null
    creditCardApplicationUrl: string | null
}

export const defaultModalState: ModalState = {
    activeCard: null,
    activeModal: null,
    creditCardApplicationUrl: null,
}

const ScoredCard = ({
    scoredCard,
    thirdPartyReference,
    scoredCategories,
}: ScoredCardProps) => {
    const appConfig = getAppConfig()

    const { appQueryState, goToRouteWithAppState } = useAppQueryState()

    const { query, isReady } = useRouter()

    const { logCardApplyEvent, logAcquisitionJourneyCardsTableClickoutEvent } =
        useLogEvents()
    const { logProductClickEvent } = useLogEvents2()
    const { trackClickout } = useInscalarTrackEvent()

    const getCardApplicationUrl = useGetCreditCardApplicationUrl()

    const [modalState, updateModalState] =
        useSetState<ModalState>(defaultModalState)

    const isNewOffer = useGetDifferenceInNewCreditCardsOffers().includes(
        scoredCard.productCode
    )
    const isPreApproved = scoredCard.isPreApproved
    const isNewSignUpJourney = query.Journey === 'true'
    const isTotallySureOffer =
        getGuaranteedOfferType({
            creditCard: scoredCard,
        }) === GuaranteedOfferType.MarketingOffer

    const activeCategoryCreditCards = useMemo(() => {
        if (isNewSignUpJourney) {
            const categoryId = appQueryState.categoryId

            const activeCategory = getCategoryByCategoryId(
                scoredCategories!,
                categoryId!
            )

            const sponsoredCreditCards = getSponsoredProducts(
                scoredCategories!,
                categoryId!
            )

            const downsellCreditCards = getDownsellCreditCards(
                scoredCategories!,
                categoryId!
            )

            const sortedCards = sortCardsBySortKey({
                cards: activeCategory?.creditCards!,
                category: categoryId!,
                sortKey: getValidCategorySortKey({
                    categoryId: categoryId!,
                    queryOption: undefined,
                }),
                categorySortKeyComparatorKeys,
            })

            return [
                ...(sponsoredCreditCards ?? []),
                ...sortedCards,
                ...(downsellCreditCards ?? []),
            ]
        }
    }, [isReady, appQueryState.categoryId, scoredCategories])

    const clickId = getUuid()

    const applicationUrl = getCardApplicationUrl({
        category: appQueryState.categoryId!,
        creditCard: scoredCard,
        creditCardIndex: 0,
        isBestMatchCard: false,
        thirdPartyReference,
        trigger: ApplicationURLTriggerLocation.Row,
        isNewOffer,
        clickId,
    })

    const onApplyPress = () => {
        logCardApplyEvent({
            category: appQueryState.categoryId!,
            clickSource: appConfig.CLICK_SOURCE_WHITELABEL,
            isBestMatchCard: false,
            productCode: scoredCard.productCode,
            rowNumber: 0,
            score: scoredCard.score,
            status: scoredCard.status,
            trigger: ApplicationURLTriggerLocation.Row,
            isCounterOffer: scoredCard.guaranteedRate?.isCounterOffer ?? false,
            isNewOffer,
        })

        trackClickout({
            step: 'credit_cards_product_details',
            journey: 'credit_cards',
            journeyCategory: 'credit_cards',
            product: scoredCard,
            productPosition: appQueryState.cardPosition
                ? Number(appQueryState.cardPosition)
                : null,
            allProducts:
                getCategoryByCategoryId(
                    scoredCategories ?? [],
                    appQueryState.categoryId!
                )?.creditCards ?? [],
        })

        logProductClickEvent({
            category: 'credit_cards',
            creditCard: scoredCard,
            productShownOrder: appQueryState.cardPosition
                ? Number(appQueryState.cardPosition)
                : null,
            productRankOrder: scoredCard.categoryScore,
            clickId,
        })

        const acquisitionClickoutEventData =
            buildAcquisitionJourneyScoredTableClickoutEventPayload(
                scoredCard,
                isNewSignUpJourney,
                activeCategoryCreditCards
            )

        if (acquisitionClickoutEventData) {
            logAcquisitionJourneyCardsTableClickoutEvent(
                acquisitionClickoutEventData
            )
        }

        window.open(applicationUrl, '_blank')
    }

    const resetModalState = () => {
        updateModalState(defaultModalState)
    }

    const productInfoText = getProductInfoText(
        scoredCard.offerHeading,
        scoredCard.offerCopy
    )

    const positives = [
        ...(scoredCard.bulletsPositive ?? []),
        ...(productInfoText ? [productInfoText] : []),
    ]

    return (
        <>
            <Box mt="space30">
                <Box mb={['space60', 'space90']}>
                    <Grid>
                        <Box gridColumn="1/13">
                            <Box
                                mb={isTotallySureOffer ? 'space30' : 'space60'}
                            >
                                <LinkWithChevron
                                    href="#"
                                    direction="left"
                                    onClick={event => {
                                        event.preventDefault()

                                        goToRouteWithAppState(
                                            appQueryState.category ??
                                                'all-cards',
                                            {
                                                queryParameters: {
                                                    cardPosition: undefined,
                                                },
                                            }
                                        )
                                    }}
                                >
                                    Back to cards
                                </LinkWithChevron>
                            </Box>
                            {isTotallySureOffer ? (
                                <Box
                                    mb={['space30', 'space60']}
                                    mx={['-space30', '0']}
                                >
                                    <ScoredCardDetailsTotallySureTooltipBanner
                                        onClick={() =>
                                            updateModalState({
                                                activeModal:
                                                    ModalType.GuaranteedOfferModal,
                                            })
                                        }
                                    />
                                </Box>
                            ) : null}
                            <BadgesRow
                                scoredCard={scoredCard}
                                isBestMatch={false}
                            />
                            <Box
                                display={['initial', 'grid']}
                                gridColumnGap="space30"
                                gridTemplateColumns={['1fr', '1fr', '1fr 12fr']}
                            >
                                <Flex
                                    gridColumnGap="space30"
                                    mb={['space10', 'space30']}
                                    justifyContent={['center', 'flex-start']}
                                >
                                    <Box width={[208, 139, 180]}>
                                        <CardImage
                                            cmsImageUrl={
                                                scoredCard.imageUrlLarge
                                            }
                                            name={scoredCard.name}
                                            isPortraitImage={getIsProductPortraitImage(
                                                scoredCard.productCode
                                            )}
                                        />
                                    </Box>
                                    <Box display={['none', 'block', 'none']}>
                                        <Box mb="space10">
                                            <Heading as="h1" variant="60">
                                                {scoredCard.name}
                                            </Heading>
                                        </Box>
                                        {isPreApproved ? (
                                            <Box width={200}>
                                                <PreApprovedBadgeWithModal />
                                            </Box>
                                        ) : (
                                            <EligibilityIndicatorWithModal
                                                score={scoredCard.score}
                                            />
                                        )}
                                    </Box>
                                </Flex>
                                <Box>
                                    <Box
                                        display={['flex', 'none', 'block']}
                                        flexDirection="column"
                                        alignItems="center"
                                        mb="space20"
                                    >
                                        <Box mb={['space20', 'space10']}>
                                            <Heading
                                                as="h1"
                                                variant="60"
                                                textAlign={['center', 'left']}
                                            >
                                                {scoredCard.name}
                                            </Heading>
                                        </Box>
                                        {isPreApproved ? (
                                            <Box width={200}>
                                                <PreApprovedBadgeWithModal />
                                            </Box>
                                        ) : (
                                            <EligibilityIndicatorWithModal
                                                score={scoredCard.score}
                                            />
                                        )}
                                    </Box>
                                    <Flex
                                        flexDirection={[
                                            'column',
                                            'column',
                                            'row',
                                        ]}
                                        mb={['space20', 'space30', '0']}
                                        alignItems={[null, 'center', 'initial']}
                                        justifyContent={[
                                            null,
                                            'center',
                                            'space-between',
                                        ]}
                                    >
                                        <Box mb={[null, 'space30', null]}>
                                            <CreditCardContent
                                                category={
                                                    appQueryState.categoryId
                                                }
                                                creditCard={scoredCard}
                                            />
                                        </Box>
                                        <Box
                                            width={280}
                                            display={['none', 'block']}
                                        >
                                            <LinkButton
                                                text="Apply"
                                                href={applicationUrl}
                                                isExternalLink
                                                variant="primary"
                                                data-testid="applyButtonDetails"
                                                rel="nofollow noopener"
                                                target="_blank"
                                                disabled={!isReady}
                                                data-gtm-event="applyNow-row-postClick"
                                                onClick={event => {
                                                    event.preventDefault()
                                                    onApplyPress()
                                                }}
                                            />
                                        </Box>
                                    </Flex>
                                    <Box mb={['space20']}>
                                        <Divider
                                            spacing="0"
                                            borderStyle="dashed"
                                            color="grey30"
                                        />
                                    </Box>
                                    {positives.length > 0 ? (
                                        <Flex
                                            gridGap="space05"
                                            flexWrap="wrap"
                                            mb={['space20', 'space30']}
                                        >
                                            <CardPositivesInfo
                                                positives={positives}
                                            />
                                        </Flex>
                                    ) : null}
                                    <RepresentativeExample
                                        item={scoredCard}
                                        onTooltipPress={() =>
                                            updateModalState({
                                                activeModal:
                                                    ModalType.RepresentativeExample,
                                            })
                                        }
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Box>
                <Box
                    backgroundColor="indigo10"
                    position="relative"
                    pt={['space30', 'space40']}
                    pb={['space40', 'space40', 'space60']}
                >
                    <Box
                        position="absolute"
                        top={['-space30', '-space30', '-space40']}
                        zIndex={1}
                        width="100%"
                    >
                        <WaveDivider waveColor="#f9f8fe" />
                    </Box>
                    <Grid>
                        <Box gridColumn="1/13">
                            <CardPageProductLists card={scoredCard} />
                        </Box>
                    </Grid>
                </Box>
                <Box backgroundColor="white" position="relative">
                    <Box
                        position="absolute"
                        top={['-space30', '-space30', '-space40']}
                        zIndex={1}
                        width="100%"
                    >
                        <WaveDivider waveColor="white" />
                    </Box>
                    <Grid>
                        <Box
                            as="section"
                            gridColumn="1/13"
                            pb={['space50', 'space70']}
                            py={['space30', 'space40']}
                        >
                            <Box mb="space20">
                                <Heading variant="60">Rates and Fees</Heading>
                            </Box>

                            <Box>
                                <RatesAndFees card={scoredCard} />
                            </Box>
                        </Box>
                    </Grid>
                </Box>
                <Box
                    display={['flex', 'none', 'none']}
                    position="fixed"
                    bottom="0"
                    left="0"
                    width="100%"
                    bg="white"
                    p="space30"
                    zIndex={2}
                    borderTop={borderStyle}
                >
                    <LinkButton
                        text="Apply"
                        href={applicationUrl}
                        isExternalLink
                        variant="primary"
                        onClick={e => {
                            e.preventDefault()
                            onApplyPress()
                        }}
                        data-testid="applyNowSticky"
                    />
                </Box>
            </Box>
            <RepresentativeExampleModal
                showModal={
                    modalState.activeModal == ModalType.RepresentativeExample
                }
                onModalClose={resetModalState}
            />
            <GuaranteedOfferModal
                showModal={
                    modalState.activeModal === ModalType.GuaranteedOfferModal
                }
                onModalClose={resetModalState}
                offerType={getGuaranteedOfferType({
                    creditCard: scoredCard,
                })}
                showOfferDurationContent={
                    isTotallySureOffer
                        ? true
                        : getShowOfferDurationContent({
                              category: appQueryState.categoryId!,
                              card: modalState.activeCard,
                          })
                }
            />
        </>
    )
}

export default ScoredCard
